import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';

import { StepConfig } from '../formbuilder/field-config';
import { FormbuilderService } from '../formbuilder/formbuilder.service';
import { ExpressionOfInterestService } from '../../../core/services/expression-of-interest.service';
import { NotifyService } from '../../../core/services/notify.service';
import {
  ExpressionOfInterestRequest,
  ExpressionOfInterestData,
  EoiModalState,
} from '../../../core/interfaces/expression-of-interest.interface';
import { EOI_FORM_CONFIG } from '../../../core/config/expression-of-interest-form.config';
import { EOI_MODAL_MESSAGES } from './expression-of-interest-modal.constants';

export interface EoiDialogData {
  licenseType?: string;
}

@Component({
  selector: 'app-expression-of-interest-modal',
  templateUrl: './expression-of-interest-modal.component.html',
  styleUrls: ['./expression-of-interest-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpressionOfInterestModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly dialogRef = inject(MatDialogRef<ExpressionOfInterestModalComponent>);
  private readonly dialogData: EoiDialogData | null = inject(MAT_DIALOG_DATA, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly formbuilderService = inject(FormbuilderService);
  private readonly eoiService = inject(ExpressionOfInterestService);
  private readonly notifyService = inject(NotifyService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  form!: FormGroup;
  steps: StepConfig[] = EOI_FORM_CONFIG;
  readonly messages = EOI_MODAL_MESSAGES;
  formColumns: 1 | 2 | 3 = 3;

  private state: EoiModalState = {
    phase: 'form',
    isSubmitting: false,
    submittedData: null,
  };

  get phase(): EoiModalState['phase'] {
    return this.state.phase;
  }

  get isSubmitting(): boolean {
    return this.state.isSubmitting;
  }

  get submittedData(): ExpressionOfInterestData | null {
    return this.state.submittedData;
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe(['(max-width: 599px)', '(max-width: 899px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const bp = this.breakpointObserver;
        this.formColumns = bp.isMatched('(max-width: 599px)') ? 1
          : bp.isMatched('(max-width: 899px)') ? 2
          : 3;
        this.cdr.markForCheck();
      });
    this.applyDialogData();
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyDialogData(): void {
    const licenseType = this.dialogData?.licenseType;
    if (!licenseType) return;

    // Clone config and patch the selectedLicense field to be pre-filled and readonly
    this.steps = EOI_FORM_CONFIG.map((step) => ({
      ...step,
      fields: step.fields.map((field) =>
        field.name === 'selectedLicense'
          ? { ...field, defaultValue: licenseType, readOnly: true }
          : field,
      ),
    }));
  }

  private initializeForm(): void {
    this.form = this.formbuilderService.buildFormFromSteps(this.steps);
  }

  onSubmitForm(formData: Record<string, any>): void {
    if (this.state.isSubmitting) return;

    this.updateState({ isSubmitting: true });

    const eoiRequest = this.extractFormData(formData);

    this.eoiService
      .submitExpressionOfInterest(eoiRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.handleSubmissionSuccess(response.data),
        error: (error) => this.handleSubmissionError(error),
      });
  }

  onCancel(): void {
    if (this.state.phase === 'success') {
      this.onDone();
      return;
    }
    this.dialogRef.close({ success: false });
  }

  onDone(): void {
    this.dialogRef.close({ success: true, data: this.state.submittedData });
  }

  private updateState(updates: Partial<EoiModalState>): void {
    this.state = { ...this.state, ...updates };
    this.cdr.markForCheck();
  }

  /**
   * Extract and validate form data
   * @param formData - Raw form data
   * @returns Structured EOI request
   */
  private extractFormData(formData: Record<string, any>): ExpressionOfInterestRequest {
    const step0Data = formData['step0'] || {};

    return {
      companyName: step0Data['companyName']?.trim() || '',
      tinNumber: step0Data['tinNumber']?.trim() || '',
      applicantEmail: step0Data['applicantEmail']?.trim() || '',
      selectedLicense: step0Data['selectedLicense'] || '',
      comments: step0Data['comments']?.trim() || '',
    };
  }

  /**
   * Handle successful form submission
   * @param data - Response data
   */
  private handleSubmissionSuccess(data: ExpressionOfInterestData): void {
    this.updateState({
      isSubmitting: false,
      phase: 'success',
      submittedData: data,
    });
  }

  /**
   * Handle form submission error
   * @param error - Error object
   */
  private handleSubmissionError(error: Error): void {
    this.updateState({ isSubmitting: false });
    this.notifyService.notifyErrorMessage(
      error.message || 'Failed to submit Expression of Interest. Please try again.',
    );
  }
}
